import React, { useEffect, useState } from 'react';
import { X, Type, AlignLeft, Shapes, Database, Archive, Tag, Layers } from 'lucide-react';
import { ingestionService } from '../../../../components/api/ingestionService';
import { apiService } from '../../../../components/api/apiService';
import { dataVaultService } from '../../../../components/api/dataVaultService';
import { embeddingProviderService, type EmbeddingProvider } from '../../../../components/api/embeddingProviderService';

const LOG_PREFIX = '[Knowledge Base]';

type CatalogOption = { id: string; label: string };
type DataVaultOption = { id: string; name: string };

function logKb(level: 'app' | 'warning' | 'error', event: string, extra?: Record<string, unknown>) {
    const payload = extra ? { event, ...extra } : { event };
    if (level === 'error') {
        console.error(LOG_PREFIX, '[error]', payload);
        return;
    }
    if (level === 'warning') {
        console.warn(LOG_PREFIX, '[warning]', payload);
        return;
    }
    console.info(LOG_PREFIX, '[app]', payload);
}

function asList(raw: unknown): Record<string, unknown>[] {
    if (Array.isArray(raw)) {
        return raw.filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object');
    }
    if (raw && typeof raw === 'object') {
        const rec = raw as Record<string, unknown>;
        return asList(rec.data ?? rec.Data ?? rec.items ?? rec.Items);
    }
    return [];
}

function vectorStoreLabel(item: Record<string, unknown>): string {
    return String(item.description || item.collectionName || item.id || 'Vector store');
}

function embeddingOptionLabel(provider: EmbeddingProvider): string {
    const display = provider.displayName?.trim() || provider.name?.trim() || 'Embedding provider';
    const model = provider.defaultModel?.trim();
    return model ? `${display} · ${model}` : display;
}

type Props = {
    onClose: () => void;
    onSubmit: (data: any) => void;
    tenantId: string;
    userId: string;
};

const KnowledgeEntityModal: React.FC<Props> = ({ onClose, onSubmit, tenantId, userId }) => {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [tags, setTags] = useState<string[]>([]);
    const [tagInput, setTagInput] = useState('');
    const [selectedVectorStoreId, setSelectedVectorStoreId] = useState('');
    const [vectorStores, setVectorStores] = useState<CatalogOption[]>([]);
    const [embeddingModels, setEmbeddingModels] = useState<CatalogOption[]>([]);
    const [embeddingModelId, setEmbeddingModelId] = useState('');
    const [catalogLoading, setCatalogLoading] = useState(false);
    const [catalogError, setCatalogError] = useState<string | null>(null);
    const [selectedVaultId, setSelectedVaultId] = useState('');
    const [dataVaults, setDataVaults] = useState<DataVaultOption[]>([]);

    useEffect(() => {
        let cancelled = false;
        const loadCatalogs = async () => {
            setCatalogLoading(true);
            setCatalogError(null);
            logKb('app', 'workspace_create_catalogs_load');
            try {
                const [vectorRaw, embeddingRaw] = await Promise.all([
                    ingestionService.getVectorStores(),
                    embeddingProviderService.getList(),
                ]);
                if (cancelled) return;
                const stores = asList(vectorRaw)
                    .map((item) => ({
                        id: String(item.id ?? item.Id ?? ''),
                        label: vectorStoreLabel(item),
                        isDefault: Boolean(item.isDefault ?? item.IsDefault),
                    }))
                    .filter((item) => item.id);
                const embeddings = asList(embeddingRaw)
                    .filter((item) => {
                        const id = String(item.id ?? item.Id ?? '');
                        const active = item.isActive !== false && item.IsActive !== false;
                        return Boolean(id) && active;
                    })
                    .map((item) => {
                        const provider = item as unknown as EmbeddingProvider;
                        return {
                            id: String(item.id ?? item.Id),
                            label: embeddingOptionLabel(provider),
                            isDefault: Boolean(item.isSystemProvider ?? item.IsSystemProvider),
                        };
                    });
                setVectorStores(stores.map(({ id, label }) => ({ id, label })));
                setEmbeddingModels(embeddings.map(({ id, label }) => ({ id, label })));
                setSelectedVectorStoreId((current) => current || stores.find((item) => item.isDefault)?.id || stores[0]?.id || '');
                setEmbeddingModelId((current) => current || embeddings.find((item) => item.isDefault)?.id || embeddings[0]?.id || '');
                logKb('app', 'workspace_create_catalogs_ok', {
                    vectorStores: stores.length,
                    embeddings: embeddings.length,
                });
                if (!stores.length || !embeddings.length) {
                    logKb('warning', 'workspace_create_catalogs_incomplete', {
                        vectorStores: stores.length,
                        embeddings: embeddings.length,
                    });
                }
            } catch (err) {
                const message = err instanceof Error ? err.message : 'Failed to load vector stores or embedding providers.';
                if (!cancelled) {
                    setVectorStores([]);
                    setEmbeddingModels([]);
                    setCatalogError(message);
                }
                logKb('error', 'workspace_create_catalogs_failed', { message });
            } finally {
                if (!cancelled) setCatalogLoading(false);
            }
        };
        void loadCatalogs();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        const loadDataVaults = async () => {
            try {
                const list = await dataVaultService.getList();
                setDataVaults((list || []).map((v) => ({ id: v.id, name: v.name })));
            } catch (err) {
                logKb('error', 'workspace_create_vaults_failed', {
                    message: err instanceof Error ? err.message : String(err),
                });
            }
        };
        loadDataVaults();
    }, []);

    const handleSubmit = async () => {
        if (!title.trim()) return alert('Title is required.');
        if (!selectedVectorStoreId || !embeddingModelId) {
            logKb('warning', 'workspace_create_blocked_missing_catalog', {
                vectorStore: Boolean(selectedVectorStoreId),
                embeddingModel: Boolean(embeddingModelId),
            });
            return alert('Select a vector store and an embedding provider.');
        }

        const payload: Record<string, unknown> = {
            tenantId,
            title: title.trim(),
            description: description.trim(),
            createdByUserId: userId,
            tags,
            vectorstoreid: selectedVectorStoreId,
            embeddingModelId,
            vaultid: selectedVaultId || null,
        };

        try {
            logKb('app', 'workspace_create_submit', {
                vectorStoreId: selectedVectorStoreId,
                embeddingModelId,
            });
            await apiService.post('/api/knowledgebase/createkbentity', payload, 'backend');
            onSubmit(payload);
            onClose();
        } catch (err) {
            logKb('error', 'workspace_create_failed', {
                message: err instanceof Error ? err.message : String(err),
            });
            alert('Failed to create knowledge entity.');
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-neutral-900 border border-neutral-700 text-white rounded-xl shadow-xl w-full max-w-md">
                <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-800">
                    <div className="flex items-center gap-2 text-sm font-semibold">
                        <Shapes className="w-4 h-4 text-indigo-400" />
                        Create Knowledge Workspace
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-white transition">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="p-4 space-y-4 text-sm">
                    <div>
                        <label className="flex items-center gap-1 text-gray-300 mb-1">
                            <Type className="w-4 h-4 text-indigo-500" />
                            Title
                        </label>
                        <input
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            className="w-full bg-neutral-800 border border-neutral-600 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            placeholder="e.g. Finance Knowledge Group"
                        />
                    </div>

                    <div>
                        <label className="flex items-center gap-1 text-gray-300 mb-1">
                            <AlignLeft className="w-4 h-4 text-indigo-500" />
                            Description
                        </label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={3}
                            className="w-full bg-neutral-800 border border-neutral-600 rounded px-3 py-1.5 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            placeholder="Short description about this entity group"
                        />
                    </div>

                    <div>
                        <label className="flex items-center gap-1 text-gray-300 mb-1">
                            <Database className="w-4 h-4 text-indigo-500" />
                            Vector store
                        </label>
                        <select
                            value={selectedVectorStoreId}
                            onChange={(e) => setSelectedVectorStoreId(e.target.value)}
                            disabled={catalogLoading}
                            className="w-full bg-neutral-800 border border-neutral-600 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-60"
                        >
                            <option value="">
                                {catalogLoading ? 'Loading...' : vectorStores.length ? 'Select vector store' : 'No vector stores found'}
                            </option>
                            {vectorStores.map((vs) => (
                                <option key={vs.id} value={vs.id}>
                                    {vs.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="flex items-center gap-1 text-gray-300 mb-1">
                            <Layers className="w-4 h-4 text-indigo-500" />
                            Embedding provider
                        </label>
                        <select
                            value={embeddingModelId}
                            onChange={(e) => setEmbeddingModelId(e.target.value)}
                            disabled={catalogLoading}
                            className="w-full bg-neutral-800 border border-neutral-600 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-60"
                        >
                            <option value="">
                                {catalogLoading ? 'Loading...' : embeddingModels.length ? 'Select embedding provider' : 'No embedding providers found'}
                            </option>
                            {embeddingModels.map((model) => (
                                <option key={model.id} value={model.id}>
                                    {model.label}
                                </option>
                            ))}
                        </select>
                        {catalogError ? <p className="mt-1 text-xs text-red-400">{catalogError}</p> : null}
                    </div>

                    <div>
                        <label className="flex items-center gap-1 text-gray-300 mb-1">
                            <Archive className="w-4 h-4 text-indigo-500" />
                            Default Data Vault
                        </label>
                        <select
                            value={selectedVaultId}
                            onChange={(e) => setSelectedVaultId(e.target.value)}
                            className="w-full bg-neutral-800 border border-neutral-600 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        >
                            <option value="">Select data vault</option>
                            {dataVaults.map((v) => (
                                <option key={v.id} value={v.id}>
                                    {v.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="flex items-center gap-1 text-gray-300 mb-1">
                            <Tag className="w-4 h-4 text-indigo-500" />
                            Tags
                        </label>
                        <div className="flex flex-wrap gap-2 min-h-[38px] bg-neutral-800 border border-neutral-600 rounded px-2 py-1.5 focus-within:ring-1 focus-within:ring-indigo-500">
                            {tags.map((tag) => (
                                <span
                                    key={tag}
                                    className="inline-flex items-center gap-1 bg-neutral-700 text-gray-200 rounded pl-2 pr-1 py-0.5 text-xs"
                                >
                                    {tag}
                                    <button
                                        type="button"
                                        onClick={() => setTags((prev) => prev.filter((t) => t !== tag))}
                                        className="text-gray-400 hover:text-white rounded p-0.5 transition"
                                        aria-label={`Remove ${tag}`}
                                    >
                                        <X className="w-3.5 h-3.5" />
                                    </button>
                                </span>
                            ))}
                            <input
                                value={tagInput}
                                onChange={(e) => setTagInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ',') {
                                        e.preventDefault();
                                        const value = tagInput.replace(/,/g, '').trim();
                                        if (value && !tags.includes(value)) {
                                            setTags((prev) => [...prev, value]);
                                            setTagInput('');
                                        }
                                    }
                                }}
                                onBlur={() => {
                                    const value = tagInput.trim();
                                    if (value && !tags.includes(value)) {
                                        setTags((prev) => [...prev, value]);
                                        setTagInput('');
                                    }
                                }}
                                className="flex-1 min-w-[120px] bg-transparent border-0 py-0.5 px-1 text-sm focus:outline-none placeholder-gray-500"
                                placeholder={tags.length === 0 ? 'Add tags (Enter or comma)' : 'Add more…'}
                            />
                        </div>
                    </div>
                </div>

                <div className="px-4 py-3 border-t border-neutral-800 flex justify-end gap-2">
                    <button
                        onClick={onClose}
                        className="text-sm px-4 py-1.5 rounded bg-neutral-700 hover:bg-neutral-600 transition"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        className="text-sm px-4 py-1.5 rounded bg-indigo-600 hover:bg-indigo-500 text-white transition"
                    >
                        Save
                    </button>
                </div>
            </div>
        </div>
    );
};

export default KnowledgeEntityModal;
